import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Home, TrendingUp, Zap, Globe, Radio, GitBranch, BookOpen, PanelLeftClose, PanelLeftOpen, LayoutGrid, ChevronRight, Layers, Monitor, ExternalLink, Sun, Moon, Settings, Scale, LogOut, ChevronUp, Plus, Pencil, Check, GripVertical, ArrowUp, ArrowDown, Trash2, X, Pin, Activity, Menu, Bell, X as XIcon, Shield, Sparkles, RotateCcw, SlidersHorizontal, Building2 } from 'lucide-react';
import { cn } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useWorkspaces, MANAGED_TEMPLATES } from '../../hooks/useWorkspaces';
import { useTheme } from '../../hooks/useTheme';
import { useChatHistory } from '../../hooks/useChatHistory';
const TEMPLATE_ICONS: Record<string, React.ElementType> = {
  cloud: Globe,
  zap: Zap,
  'bar-chart': TrendingUp,
  'trending-up': Activity
};
const PINNABLE_PAGES: Record<string, {
  label: string;
  to: string;
  icon: React.ElementType;
}> = {
  intraday: {
    label: 'Intraday Trader',
    to: '/intraday',
    icon: Activity
  }
};
function loadPinnedPages(): string[] {
  try {
    return JSON.parse(localStorage.getItem('jua-pinned-pages') || '[]');
  } catch {
    return [];
  }
}
export function Sidebar({
  isOpen,
  onToggle
}: {
  isOpen: boolean;
  onToggle: () => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    theme,
    setTheme
  } = useTheme();
  const {
    workspaces,
    setActiveWorkspace,
    createWorkspace,
    deleteWorkspace,
    togglePin,
    reorderWorkspaces
  } = useWorkspaces();
  const {
    handleNewChat
  } = useChatHistory();
  const pinnedWorkspaces = workspaces.filter(w => w.pinned);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isEditingWorkspaces, setIsEditingWorkspaces] = useState(false);
  const [pinnedPages, setPinnedPages] = useState<string[]>(loadPinnedPages);
  const [activeOrg, setActiveOrg] = useState('Jua.ai');
  const userButtonRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  // Sync pinned pages from localStorage when location changes
  useEffect(() => {
    setPinnedPages(loadPinnedPages());
  }, [location.pathname]);
  const unpinPage = (key: string) => {
    const updated = pinnedPages.filter(p => p !== key);
    localStorage.setItem('jua-pinned-pages', JSON.stringify(updated));
    setPinnedPages(updated);
  };
  // Close user menu on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (userButtonRef.current && !userButtonRef.current.contains(target) && userMenuRef.current && !userMenuRef.current.contains(target)) {
        setIsUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const primaryItems = [{
    icon: Sparkles,
    label: 'Athena',
    to: '/athena'
  }, {
    icon: LayoutGrid,
    label: 'Workspaces',
    to: '/workspaces',
    badge: 'NEW'
  }];
  const moreItems = [{
    icon: TrendingUp,
    label: 'Weather Forecast',
    to: '/energy'
  }, {
    icon: Globe,
    label: 'Map',
    to: '/map'
  }, {
    icon: Radio,
    label: 'Stations',
    to: '/stations',
    badge: 'BETA'
  }];
  const moreIsActive = moreItems.some(item => location.pathname === item.to);
  const [moreOpen, setMoreOpen] = useState(moreIsActive);
  const renderNavItem = (item: (typeof primaryItems)[0] & {
    badge?: string;
  }) => <NavLink key={item.to} to={item.to} end={item.to === '/athena'} onClick={() => {
    if (item.to === '/athena') {
      handleNewChat();
    }
  }} className={({
    isActive
  }) => cn('flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 group relative', item.to === '/athena' && 'jua-athena-link', isActive ? 'text-[#EC4899] bg-[#EC4899]/5' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50')} data-id="element-810">
      {({
      isActive
    }) => <>
          <item.icon size={18} className={isActive ? 'text-[#EC4899]' : 'text-slate-500 group-hover:text-slate-300'} data-id="element-811" />
          <span className="flex-1 text-left leading-tight" data-id="element-812">{item.label}</span>
          {item.badge && <Badge variant="pink" className="ml-auto text-[10px] px-1.5 py-0 h-4 flex items-center" data-id="element-813">
              {item.badge}
            </Badge>}
        </>}
    </NavLink>;
  return <aside className={cn('app-sidebar fixed left-0 top-0 z-40 h-screen w-[240px] border-r border-slate-800/40 bg-[#0F172A] flex flex-col transition-transform duration-300 ease-in-out', isOpen ? 'translate-x-0' : '-translate-x-full')} data-id="element-814">
      {/* Header */}
      <div className="sidebar-header flex h-16 items-center justify-between px-4 border-b border-slate-800/50" data-id="element-815">
        <NavLink to="/home" className="flex items-center gap-2" data-id="element-816">
          <img src="https://cdn.magicpatterns.com/uploads/iXnnzvi4wVFko9E21giff4/pasted-image.png" alt="Jua logo" className="w-8 h-8 object-contain flex-shrink-0" data-id="element-817" />
          <span className={cn('text-2xl font-light tracking-tight', theme === 'light' ? 'text-slate-800' : 'text-white')} data-id="element-818">
            Jua
          </span>
        </NavLink>
        <button onClick={onToggle} className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-800/60 transition-colors" title="Collapse sidebar" data-id="element-819">
          <PanelLeftClose size={18} data-id="element-820" />
        </button>
      </div>

      {/* Primary Nav */}
      <nav className="jua-sidebar-nav flex-1 overflow-y-auto py-4 px-2 space-y-1" data-id="element-821">
        <div className="jua-sidebar-pages-top space-y-1" data-id="element-822">
          {primaryItems.map(renderNavItem)}

          {/* Managed Pages */}
          <div className="px-3 pt-4 pb-1" data-id="element-823">
            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest" data-id="element-824">
              Pages
            </span>
          </div>
          {moreItems.map(renderNavItem)}
        </div>

        {/* Pinned Workspaces */}
        {false && (pinnedWorkspaces.length > 0 || pinnedPages.length > 0) && <>
            <div className="jua-sidebar-pinned" data-id="element-825">
              <div className="px-3 pt-4 pb-1 flex items-center justify-between" data-id="element-826">
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest" data-id="element-827">
                  Pinned
                </span>
                <button onClick={() => setIsEditingWorkspaces(!isEditingWorkspaces)} className={cn('p-1 rounded-md transition-colors', isEditingWorkspaces ? 'text-[#EC4899] bg-[#EC4899]/10 hover:bg-[#EC4899]/20' : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/50')} title={isEditingWorkspaces ? 'Done editing' : 'Edit workspaces'} data-id="element-828">
                  {isEditingWorkspaces ? <Check size={12} data-id="element-829" /> : <Pencil size={12} data-id="element-830" />}
                </button>
              </div>

              {/* Pinned Pages (e.g. Intraday Trader) */}
              {pinnedPages.map(key => {
            const page = PINNABLE_PAGES[key];
            if (!page) return null;
            const PageIcon = page.icon;
            return <div key={key} className="relative flex items-center group" data-id="element-831">
                    {isEditingWorkspaces ? <div className="flex items-center w-full px-1 py-1 rounded-md" data-id="element-832">
                        <div className="flex-1 flex items-center gap-2 px-2 py-1.5 min-w-0" data-id="element-833">
                          <PageIcon size={14} className="text-[#EC4899] flex-shrink-0" data-id="element-834" />
                          <span className="flex-1 text-sm font-medium text-slate-300 truncate" data-id="element-835">
                            {page.label}
                          </span>
                        </div>
                        <button onClick={() => unpinPage(key)} className="p-1.5 rounded-md text-[#EC4899] hover:text-[#EC4899]/60 hover:bg-[#EC4899]/10 transition-colors flex-shrink-0" title="Unpin from sidebar" data-id="element-836">
                          <Pin size={12} className="fill-current" data-id="element-837" />
                        </button>
                      </div> : <NavLink to={page.to} className={({
                isActive
              }) => cn('flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200 group relative w-full', isActive ? 'text-[#EC4899] bg-[#EC4899]/5' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50')} data-id="element-838">
                        {({
                  isActive
                }) => <>
                            <PageIcon size={16} className={cn('flex-shrink-0', isActive ? 'text-[#EC4899]' : 'text-slate-500 group-hover:text-slate-300')} data-id="element-839" />
                            <span className="flex-1 text-left leading-tight truncate" data-id="element-840">
                              {page.label}
                            </span>
                            <button onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    unpinPage(key);
                  }} className="opacity-0 group-hover:opacity-100 p-1 rounded-md text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-all flex-shrink-0" title="Unpin from sidebar" data-id="element-841">
                              <X size={12} data-id="element-842" />
                            </button>
                          </>}
                      </NavLink>}
                  </div>;
          })}

              {pinnedWorkspaces.map(ws => {
            const globalIndex = workspaces.findIndex(w => w.id === ws.id);
            const pinnedIndex = pinnedWorkspaces.findIndex(w => w.id === ws.id);
            return <div key={ws.id} className="relative flex items-center group" data-id="element-843">
                    {isEditingWorkspaces ? <div className="flex items-center w-full px-1 py-1 rounded-md" data-id="element-844">
                        {/* Drag handle */}
                        <div className="flex items-center justify-center mr-1 p-1 cursor-grab active:cursor-grabbing text-slate-600 hover:text-slate-400 transition-colors" title="Drag to reorder" data-id="element-845">
                          <GripVertical size={14} data-id="element-846" />
                        </div>

                        {/* Reorder controls */}
                        <div className="flex flex-col mr-1" data-id="element-847">
                          <button onClick={() => {
                    if (pinnedIndex > 0) {
                      const prevGlobalIndex = workspaces.findIndex(w => w.id === pinnedWorkspaces[pinnedIndex - 1].id);
                      reorderWorkspaces(globalIndex, prevGlobalIndex);
                    }
                  }} disabled={pinnedIndex === 0} className={cn('p-0.5 rounded transition-colors', pinnedIndex === 0 ? 'text-slate-700 cursor-not-allowed' : 'text-slate-500 hover:text-white hover:bg-slate-700')} data-id="element-848">
                            <ArrowUp size={10} data-id="element-849" />
                          </button>
                          <button onClick={() => {
                    if (pinnedIndex < pinnedWorkspaces.length - 1) {
                      const nextGlobalIndex = workspaces.findIndex(w => w.id === pinnedWorkspaces[pinnedIndex + 1].id);
                      reorderWorkspaces(globalIndex, nextGlobalIndex);
                    }
                  }} disabled={pinnedIndex === pinnedWorkspaces.length - 1} className={cn('p-0.5 rounded transition-colors', pinnedIndex === pinnedWorkspaces.length - 1 ? 'text-slate-700 cursor-not-allowed' : 'text-slate-500 hover:text-white hover:bg-slate-700')} data-id="element-850">
                            <ArrowDown size={10} data-id="element-851" />
                          </button>
                        </div>

                        {/* Workspace name */}
                        <div className="flex-1 flex items-center gap-2 px-2 py-1.5 min-w-0" data-id="element-852">
                          <div className="h-[18px] w-[18px] rounded flex items-center justify-center text-[10px] font-bold flex-shrink-0 bg-slate-700/50 text-slate-400" data-id="element-853">
                            {ws.name.charAt(0).toUpperCase()}
                          </div>
                          <span className="flex-1 text-sm font-medium text-slate-300 truncate" data-id="element-854">
                            {ws.name}
                          </span>
                        </div>

                        {/* Unpin / Delete */}
                        <button onClick={() => togglePin(ws.id)} className="p-1.5 rounded-md text-[#EC4899] hover:text-[#EC4899]/60 hover:bg-[#EC4899]/10 transition-colors flex-shrink-0" title="Unpin from sidebar" data-id="element-855">
                          <Pin size={12} className="fill-current" data-id="element-856" />
                        </button>
                        <button onClick={() => {
                  if (workspaces.length > 1) {
                    deleteWorkspace(ws.id);
                  }
                }} disabled={workspaces.length <= 1} className={cn('p-1.5 rounded-md transition-colors flex-shrink-0', workspaces.length <= 1 ? 'text-slate-700 cursor-not-allowed' : 'text-slate-500 hover:text-red-400 hover:bg-red-500/10')} title="Delete workspace" data-id="element-857">
                          <Trash2 size={12} data-id="element-858" />
                        </button>
                      </div> : <NavLink to={`/workspace/${ws.id}`} onClick={() => setActiveWorkspace(ws.id)} className={({
                isActive
              }) => cn('flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200 group relative w-full', isActive ? 'text-[#EC4899] bg-[#EC4899]/5' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50')} data-id="element-859">
                        {({
                  isActive
                }) => <>
                            <div className={cn('h-[18px] w-[18px] rounded flex items-center justify-center text-[10px] font-bold flex-shrink-0', isActive ? 'bg-[#EC4899]/20 text-[#EC4899]' : 'bg-slate-700/50 text-slate-400 group-hover:bg-slate-700 group-hover:text-slate-300')} data-id="element-860">
                              {ws.name.charAt(0).toUpperCase()}
                            </div>
                            <span className="flex-1 text-left leading-tight truncate" data-id="element-861">
                              {ws.name}
                            </span>
                          </>}
                      </NavLink>}
                  </div>;
          })}

              {!isEditingWorkspaces && <div className="pt-4" data-id="element-862">
                  <button onClick={() => {
              navigate('/workspaces', {
                state: {
                  openModal: true
                }
              });
            }} className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-slate-400 hover:text-white border border-dashed border-slate-700 hover:border-[#EC4899]/40 hover:bg-[#EC4899]/5 transition-all duration-200 w-full" data-id="element-863">
                    <Plus size={16} className="text-slate-500" data-id="element-864" />
                    <span data-id="element-865">Add workspace</span>
                  </button>
                </div>}
            </div>
          </>}
      </nav>

      {/* Alert Rules */}
      <div className="px-2 pb-2" data-id="element-866">
        <NavLink to="/alerts" className={({
        isActive
      }) => cn('flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 group relative', isActive ? 'text-[#EC4899] bg-[#EC4899]/5' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50')} data-id="element-867">
          {({
          isActive
        }) => <>
              <Bell size={18} className={isActive ? 'text-[#EC4899]' : 'text-slate-500 group-hover:text-slate-300'} data-id="element-868" />
              <span className="flex-1 text-left leading-tight" data-id="element-869">
                Manage Alerts
              </span>
            </>}
        </NavLink>
      </div>

      {/* Footer/User — Account button with dropdown */}
      <div className="sidebar-footer p-3 border-t border-slate-800/50 relative" ref={userButtonRef} data-id="element-870">
        <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className={cn('w-full flex items-center gap-3 px-2 py-2 rounded-lg transition-colors jua-user-footer-btn', isUserMenuOpen ? 'bg-slate-800/70 text-white' : 'hover:bg-slate-800/50 text-white/90')} data-id="element-871">
          <img src="https://i.pravatar.cc/150?img=68" alt="User avatar" className="h-8 w-8 rounded-full object-cover shadow-md shadow-pink-900/30 flex-shrink-0 ring-2 ring-[#EC4899]/30" data-id="element-872" />
          <div className="flex-1 min-w-0 text-left" data-id="element-873">
            <p className="text-sm font-medium truncate" data-id="element-874">John Doe</p>
            <p className="text-[11px] text-slate-500 truncate" data-id="element-875">
              john@example.com
            </p>
          </div>
          <ChevronUp size={14} className={cn('text-slate-500 transition-transform duration-200 flex-shrink-0', isUserMenuOpen ? 'rotate-0' : 'rotate-180')} data-id="element-876" />
        </button>

        {isUserMenuOpen && ReactDOM.createPortal(<div ref={userMenuRef} className="fixed w-[220px] rounded-xl shadow-2xl shadow-black/50 border border-white/[0.08] overflow-hidden jua-user-menu" style={{
        bottom: 72,
        left: 12,
        zIndex: 9999,
        background: 'rgba(15, 23, 42, 0.92)',
        backdropFilter: 'blur(40px) saturate(180%)',
        WebkitBackdropFilter: 'blur(40px) saturate(180%)'
      }} data-id="element-877">
              <div className="absolute inset-0 rounded-xl pointer-events-none" style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 50%, rgba(255,255,255,0.02) 100%)'
        }} data-id="element-878" />

              {/* Platform */}
              <div className="relative px-3 py-2 border-b border-white/[0.06]" data-id="element-879">
                <a href="#" className="flex items-center justify-between gap-2 py-1.5 text-white/60 hover:text-white/90 transition-colors group" data-id="element-880">
                  <div className="flex items-center gap-2.5" data-id="element-881">
                    <Monitor size={14} className="text-white/50" data-id="element-882" />
                    <span className="text-xs" data-id="element-883">Developer Platform</span>
                  </div>
                  <ExternalLink size={12} className="text-white/25 group-hover:text-white/50 transition-colors" data-id="element-884" />
                </a>
              </div>

              {/* Organization Switcher */}
              <div className={cn('relative px-1.5 py-1.5 border-b', theme === 'light' ? 'border-black/[0.06]' : 'border-white/[0.06]')} data-id="element-885">
                <div className={cn('px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider', theme === 'light' ? 'text-slate-400' : 'text-white/40')} data-id="element-886">
                  Switch Organization
                </div>
                <button onClick={() => {
            setActiveOrg('Jua.ai');
            setIsUserMenuOpen(false);
          }} className={cn('flex items-center justify-between w-full px-2.5 py-2 rounded-lg transition-colors text-xs', theme === 'light' ? 'text-slate-600 hover:text-slate-900 hover:bg-black/[0.04]' : 'text-white/70 hover:text-white hover:bg-white/[0.06]')} data-id="element-887">
                  <div className="flex items-center gap-2.5" data-id="element-888">
                    <Building2 size={14} className={theme === 'light' ? 'text-slate-400' : 'text-white/50'} data-id="element-889" />
                    <span className={activeOrg === 'Jua.ai' ? cn('font-medium', theme === 'light' ? 'text-slate-900' : 'text-white') : ''} data-id="element-890">
                      Jua.ai
                    </span>
                  </div>
                  {activeOrg === 'Jua.ai' && <Check size={12} className="text-[#EC4899]" data-id="element-891" />}
                </button>
                <button onClick={() => {
            setActiveOrg('Jua Zurich');
            setIsUserMenuOpen(false);
          }} className={cn('flex items-center justify-between w-full px-2.5 py-2 rounded-lg transition-colors text-xs', theme === 'light' ? 'text-slate-600 hover:text-slate-900 hover:bg-black/[0.04]' : 'text-white/70 hover:text-white hover:bg-white/[0.06]')} data-id="element-892">
                  <div className="flex items-center gap-2.5" data-id="element-893">
                    <Building2 size={14} className={theme === 'light' ? 'text-slate-400' : 'text-white/50'} data-id="element-894" />
                    <span className={activeOrg === 'Jua Zurich' ? cn('font-medium', theme === 'light' ? 'text-slate-900' : 'text-white') : ''} data-id="element-895">
                      Jua Zurich
                    </span>
                  </div>
                  {activeOrg === 'Jua Zurich' && <Check size={12} className="text-[#EC4899]" data-id="element-896" />}
                </button>
              </div>

              {/* Theme Toggle */}
              <div className="relative px-3 py-2.5 border-b border-white/[0.06]" data-id="element-897">
                <div className="jua-theme-toggle flex items-center gap-1 bg-white/[0.06] rounded-lg p-0.5 w-fit" data-id="element-898">
                  <button onClick={() => setTheme('light')} className={cn('sidebar-ignore p-1.5 rounded-md transition-colors', theme === 'light' ? 'bg-white/[0.15] text-amber-400' : 'text-white/30 hover:text-white/60 hover:bg-white/[0.04]')} title="Light theme" data-id="element-899">
                    <Sun size={12} data-id="element-900" />
                  </button>
                  <button onClick={() => setTheme('dark')} className={cn('sidebar-ignore p-1.5 rounded-md transition-colors', theme === 'dark' ? 'bg-white/[0.08] text-white/70' : 'text-white/30 hover:text-white/60 hover:bg-white/[0.04]')} title="Dark theme" data-id="element-901">
                    <Moon size={12} data-id="element-902" />
                  </button>
                </div>
              </div>

              {/* Menu Items */}
              <div className="relative px-1.5 py-1.5 border-b border-white/[0.06]" data-id="element-903">
                <button onClick={() => {
            setIsUserMenuOpen(false);
            navigate('/settings');
          }} className="flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-white/70 hover:text-white hover:bg-white/[0.06] transition-colors text-xs" data-id="element-904">
                  <Settings size={14} className="text-white/50" data-id="element-905" />
                  Account Settings
                </button>
                <button onClick={() => {
            setIsUserMenuOpen(false);
            navigate('/settings');
          }} className="flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-white/70 hover:text-white hover:bg-white/[0.06] transition-colors text-xs" data-id="element-906">
                  <SlidersHorizontal size={14} className="text-white/50" data-id="element-907" />
                  Onboarding Preferences
                </button>
                <button onClick={() => {
            setIsUserMenuOpen(false);
            localStorage.removeItem('jua-tour-completed');
            navigate('/athena');
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('jua-restart-tour'));
            }, 500);
          }} className="flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-white/70 hover:text-white hover:bg-white/[0.06] transition-colors text-xs" data-id="element-908">
                  <RotateCcw size={14} className="text-white/50" data-id="element-909" />
                  Replay Product Tour
                </button>
                <button className="flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-white/70 hover:text-white hover:bg-white/[0.06] transition-colors text-xs" data-id="element-910">
                  <Scale size={14} className="text-white/50" data-id="element-911" />
                  Legal Terms
                </button>
              </div>

              {/* Logout */}
              <div className="relative px-1.5 py-1.5" data-id="element-912">
                <button className="flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/[0.08] transition-colors text-xs" data-id="element-913">
                  <LogOut size={14} data-id="element-914" />
                  Logout
                </button>
              </div>
            </div>, document.body)}
      </div>
    </aside>;
}